import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useActiveClinicaId } from "@/hooks/useActiveClinicaId";
import { parseMoneyBR } from "@/lib/money";
import { apiList } from "@/lib/api";
import { normalizeLocalDate, toLocalISODate } from "@/lib/date-utils";
import type { FluxoCaixa } from "@/hooks/useFluxoCaixa";
import type { ContaReceber } from "@/hooks/useContasReceber";
import type { ContaPagar } from "@/hooks/useContasPagar";
import type { Comissao } from "@/hooks/useComissoes";
import type { PagamentoFuncionario } from "@/hooks/usePagamentosFuncionarios";

type PagamentoPendente = { valor?: number; status?: string };

export interface MovimentoRelatorio {
  id: string;
  data: string;
  tipo: "Entrada" | "Saída";
  categoria: string;
  descricao: string;
  valor: number;
  valor_bruto?: number;
  taxa_operadora_percentual?: number;
  valor_taxa_operadora?: number;
  forma_pagamento?: string;
  origem: string;
  responsavel?: string;
  paciente?: string;
}

export interface DadosFinanceiros {
  receitas_total: number;
  despesas_total: number;
  saldo: number;
  contas_receber_pendente: number;
  contas_pagar_pendente: number;
  pagamentos_compensar: number;
  receitas_por_categoria: { categoria: string; valor: number }[];
  despesas_por_categoria: { categoria: string; valor: number }[];
  fluxo_mensal: { mes: string; receitas: number; despesas: number }[];
  movimentacoes: MovimentoRelatorio[];
}

const emptyData: DadosFinanceiros = {
  receitas_total: 0,
  despesas_total: 0,
  saldo: 0,
  contas_receber_pendente: 0,
  contas_pagar_pendente: 0,
  pagamentos_compensar: 0,
  receitas_por_categoria: [],
  despesas_por_categoria: [],
  fluxo_mensal: [],
  movimentacoes: [],
};

const money = (value: unknown) => parseMoneyBR(value);

const originLabel = (mov: FluxoCaixa) => {
  if (mov.subtipo === "sangria" || mov.categoria === "Sangria") return "Sangria";
  if (mov.origem === "recibo") return "Recibo";
  if (mov.origem === "estorno_recibo") return "Estorno de recibo";
  return "Fluxo de caixa";
};

export const useRelatorioFinanceiro = (dataInicio?: Date, dataFim?: Date) => {
  const { user } = useAuth();
  const activeClinicaId = useActiveClinicaId();
  const [loading, setLoading] = useState(true);
  const [dadosFinanceiros, setDadosFinanceiros] = useState<DadosFinanceiros>(emptyData);

  const fetchRelatorioFinanceiro = async () => {
    if (!user) {
      setDadosFinanceiros(emptyData);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const [fluxo, contasReceber, contasPagar, comissoes, pagamentosFuncionarios, pagamentosPendentes] = await Promise.all([
        apiList<FluxoCaixa>("fluxo_caixa"),
        apiList<ContaReceber>("contas_receber"),
        apiList<ContaPagar>("contas_pagar"),
        apiList<Comissao>("comissoes"),
        apiList<PagamentoFuncionario>("pagamentos_funcionarios"),
        apiList<PagamentoPendente>("cheques"),
      ]);

      const inicio = dataInicio ? toLocalISODate(dataInicio) : "0000-01-01";
      const fim = dataFim ? toLocalISODate(dataFim) : "9999-12-31";
      const dentroPeriodo = (value?: string | null) => {
        const date = normalizeLocalDate(value);
        return !!date && date >= inicio && date <= fim;
      };
      const represented = new Set(fluxo.map((item) => `${item.origem || ""}:${item.origem_id || ""}`));

      const movimentos: MovimentoRelatorio[] = fluxo
        .filter((item) => item.status !== "Cancelado" && dentroPeriodo(item.data_movimentacao))
        .map((item) => ({
          id: item.id,
          data: normalizeLocalDate(item.data_movimentacao),
          tipo: item.tipo,
          categoria: item.categoria || "Sem categoria",
          descricao: item.descricao || "Movimentação financeira",
          valor: money(item.valor_liquido ?? item.valor),
          valor_bruto: money(item.valor),
          taxa_operadora_percentual: money(item.taxa_operadora_percentual),
          valor_taxa_operadora: money(item.valor_taxa_operadora),
          forma_pagamento: item.forma_pagamento,
          origem: originLabel(item),
          responsavel: item.responsavel_nome,
          paciente: item.paciente_nome,
        }));

      contasReceber
        .filter((item) => item.status === "Recebida" && dentroPeriodo(item.data_recebimento) && !represented.has(`conta_receber:${item.id}`))
        .forEach((item) => movimentos.push({
          id: `conta-receber-${item.id}`,
          data: normalizeLocalDate(item.data_recebimento),
          tipo: "Entrada",
          categoria: item.categoria || "Conta recebida",
          descricao: item.descricao || "Conta recebida",
          valor: money(item.valor_liquido ?? item.valor),
          valor_bruto: money(item.valor),
          taxa_operadora_percentual: money(item.taxa_operadora_percentual),
          valor_taxa_operadora: money(item.valor_taxa_operadora),
          forma_pagamento: item.forma_pagamento,
          origem: "Conta a receber",
        }));

      contasPagar
        .filter((item) => item.status === "Paga" && dentroPeriodo(item.data_pagamento) && !represented.has(`conta_pagar:${item.id}`))
        .forEach((item) => movimentos.push({
          id: `conta-pagar-${item.id}`,
          data: normalizeLocalDate(item.data_pagamento),
          tipo: "Saída",
          categoria: item.categoria || "Conta paga",
          descricao: item.descricao || "Conta paga",
          valor: money(item.valor),
          forma_pagamento: item.forma_pagamento,
          origem: "Conta a pagar",
        }));

      comissoes
        .filter((item) => item.status === "Paga" && dentroPeriodo(item.data_pagamento) && !represented.has(`comissao:${item.id}`))
        .forEach((item) => movimentos.push({
          id: `comissao-${item.id}`,
          data: normalizeLocalDate(item.data_pagamento),
          tipo: "Saída",
          categoria: "Comissão",
          descricao: item.referencia || "Comissão paga",
          valor: money(item.valor_comissao),
          forma_pagamento: item.forma_pagamento,
          origem: "Comissão",
        }));

      pagamentosFuncionarios
        .filter((item) => item.status === "Pago" && dentroPeriodo(item.data_pagamento) && !represented.has(`pagamento_funcionario:${item.id}`))
        .forEach((item) => movimentos.push({
          id: `pagamento-funcionario-${item.id}`,
          data: normalizeLocalDate(item.data_pagamento),
          tipo: "Saída",
          categoria: "Pagamento de funcionário",
          descricao: `Pagamento - ${item.funcionario_nome || "funcionário"}`,
          valor: money(item.valor_total),
          forma_pagamento: item.forma_pagamento,
          origem: "Pagamento de funcionário",
        }));

      movimentos.sort((a, b) => b.data.localeCompare(a.data) || b.id.localeCompare(a.id));
      const receitas = movimentos.filter((item) => item.tipo === "Entrada");
      const despesas = movimentos.filter((item) => item.tipo === "Saída");
      const receitasTotal = receitas.reduce((sum, item) => sum + item.valor, 0);
      const despesasTotal = despesas.reduce((sum, item) => sum + item.valor, 0);
      const agruparCategoria = (items: MovimentoRelatorio[]) => Array.from(
        items.reduce((map, item) => map.set(item.categoria, (map.get(item.categoria) || 0) + item.valor), new Map<string, number>())
      ).map(([categoria, valor]) => ({ categoria, valor })).sort((a, b) => b.valor - a.valor);
      const mensal = new Map<string, { mes: string; receitas: number; despesas: number }>();
      movimentos.forEach((item) => {
        const key = item.data.slice(0, 7);
        const label = new Intl.DateTimeFormat("pt-BR", { month: "short", year: "numeric", timeZone: "America/Manaus" })
          .format(new Date(`${key}-15T12:00:00`));
        const current = mensal.get(key) || { mes: label, receitas: 0, despesas: 0 };
        if (item.tipo === "Entrada") current.receitas += item.valor;
        else current.despesas += item.valor;
        mensal.set(key, current);
      });

      setDadosFinanceiros({
        receitas_total: receitasTotal,
        despesas_total: despesasTotal,
        saldo: receitasTotal - despesasTotal,
        contas_receber_pendente: contasReceber.filter((item) => item.status === "Pendente").reduce((sum, item) => sum + money(item.valor), 0),
        contas_pagar_pendente: contasPagar.filter((item) => item.status === "Pendente").reduce((sum, item) => sum + money(item.valor), 0),
        pagamentos_compensar: pagamentosPendentes.filter((item) => item.status === "A Compensar").reduce((sum, item) => sum + money(item.valor), 0),
        receitas_por_categoria: agruparCategoria(receitas),
        despesas_por_categoria: agruparCategoria(despesas),
        fluxo_mensal: Array.from(mensal.entries()).sort(([a], [b]) => a.localeCompare(b)).map(([, value]) => value),
        movimentacoes: movimentos,
      });
    } catch (error: unknown) {
      console.error("Erro ao buscar relatório financeiro:", error);
      setDadosFinanceiros(emptyData);
      toast.error("Erro ao carregar relatório financeiro: " + (error instanceof Error ? error.message : "erro inesperado"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRelatorioFinanceiro();
  }, [user?.id, activeClinicaId, dataInicio?.getTime(), dataFim?.getTime()]);

  return { dadosFinanceiros, loading, refetch: fetchRelatorioFinanceiro };
};
